import { createLegacyAssetAppearance } from '../../plugin-contracts/asset_binding_contract.mjs';

// Browser counterparts of the backend's recolorGlbBuffer()/recolorSvgRegions()
// (backend/asset_objects.js). They must stay byte-identical to the backend so a
// pinned colour state reproduces exactly what the current asset files show;
// test/logic/legacy_asset_appearance.test.mjs compares both implementations.

const FILL_GROUP = /(<g class="fill-regions"[^>]*>)([\s\S]*?)(<\/g>)/;
const FILL_PATH = /<path\b[^>]*\/>/g;
const FILL = /fill="(#[0-9a-fA-F]{6})"/;

export function recolorLegacySvgRegions(svgText, regionColors) {
  let index = -1;
  return svgText.replace(FILL_GROUP, (_full, openTag, inner, closeTag) => {
    const recoloredInner = inner.replace(FILL_PATH, pathTag => {
      index += 1;
      const color = regionColors[String(index)];
      if (!color) return pathTag;
      return pathTag.replace(/fill="#[0-9a-fA-F]{6}"/, `fill="${color}"`);
    });
    return `${openTag}${recoloredInner}${closeTag}`;
  });
}

/** Fill colour per region in recolouring order; null for a region without a fill. */
export function legacySvgRegionFills(svgText) {
  const group = FILL_GROUP.exec(svgText);
  return group ? (group[2].match(FILL_PATH) || []).map(path => FILL.exec(path)?.[1] ?? null) : [];
}

/**
 * Region colours that turn the pristine SVG into the currently served one. Throws
 * when the served file is not exactly such a recolouring (other geometry, other
 * revision), so a pin never records a guessed colour state.
 */
export function deriveLegacySvgRegionColors(pristineText, servedText) {
  const pristine = legacySvgRegionFills(pristineText), served = legacySvgRegionFills(servedText);
  if (pristine.length !== served.length) throw new TypeError('Legacy appearance: region count differs');
  const colors = {};
  served.forEach((color, index) => {
    if (color === pristine[index]) return;
    if (!color || color !== color.toUpperCase()) throw new TypeError('Legacy appearance: non-canonical region colour');
    colors[String(index)] = color;
  });
  if (recolorLegacySvgRegions(pristineText, colors) !== servedText) throw new TypeError('Legacy appearance: served SVG is not a recolouring');
  return colors;
}

function hexToUnitRgb(hex) {
  const clean = String(hex || '').replace('#', '');
  return [0, 2, 4].map(start => parseInt(clean.slice(start, start + 2), 16) / 255);
}

const ascii = bytes => String.fromCharCode(...bytes);
const utf8Length = text => new TextEncoder().encode(text).length;

export function recolorLegacyGlbBytes(bytes, hexColor) {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (bytes.length < 12 || ascii(bytes.subarray(0, 4)) !== 'glTF') throw new TypeError('Keine gueltige GLB-Datei (Magic fehlt).');
  const version = view.getUint32(4, true);
  const chunks = [];
  let offset = 12;
  while (offset + 8 <= bytes.length) {
    const chunkLength = view.getUint32(offset, true);
    chunks.push({ type: ascii(bytes.subarray(offset + 4, offset + 8)), data: bytes.subarray(offset + 8, offset + 8 + chunkLength) });
    offset += 8 + chunkLength;
  }
  const jsonIndex = chunks.findIndex(chunk => chunk.type === 'JSON');
  if (jsonIndex === -1) throw new TypeError('GLB enthaelt keinen JSON-Chunk.');
  const json = JSON.parse(new TextDecoder('utf-8').decode(chunks[jsonIndex].data));
  const [r, g, b] = hexToUnitRgb(hexColor);
  if (Array.isArray(json.materials) && json.materials.length) {
    for (const material of json.materials) {
      material.pbrMetallicRoughness ||= {};
      const previous = material.pbrMetallicRoughness.baseColorFactor;
      const alpha = Array.isArray(previous) && previous.length === 4 ? previous[3] : 1;
      material.pbrMetallicRoughness.baseColorFactor = [r, g, b, alpha];
      material.pbrMetallicRoughness.metallicFactor = 0;
      material.pbrMetallicRoughness.roughnessFactor = 1;
    }
  } else {
    json.materials = [{ name: 'ColorOverride', pbrMetallicRoughness: { baseColorFactor: [r, g, b, 1], metallicFactor: 0, roughnessFactor: 1 } }];
    for (const mesh of Array.isArray(json.meshes) ? json.meshes : []) {
      for (const primitive of Array.isArray(mesh.primitives) ? mesh.primitives : []) {
        if (primitive.material == null) primitive.material = 0;
      }
    }
  }
  let jsonText = JSON.stringify(json);
  while (utf8Length(jsonText) % 4 !== 0) jsonText += ' ';
  chunks[jsonIndex] = { type: 'JSON', data: new TextEncoder().encode(jsonText) };
  const total = 12 + chunks.reduce((sum, chunk) => sum + 8 + chunk.data.length, 0);
  const out = new Uint8Array(total), outView = new DataView(out.buffer);
  out.set([0x67, 0x6c, 0x54, 0x46]); outView.setUint32(4, version, true); outView.setUint32(8, total, true);
  let position = 12;
  for (const chunk of chunks) {
    outView.setUint32(position, chunk.data.length, true);
    for (let i = 0; i < 4; i++) out[position + 4 + i] = chunk.type.charCodeAt(i) & 0xff;
    out.set(chunk.data, position + 8);
    position += 8 + chunk.data.length;
  }
  return out;
}

/** Apply a validated colour state to verified compatibility artefact bytes. */
export function applyLegacyAppearance(kind, bytes, appearance, viewId = null) {
  if (!appearance) return bytes;
  const state = createLegacyAssetAppearance(appearance);
  if (kind === 'legacyGlb') return state.legacyGlbColor ? recolorLegacyGlbBytes(bytes, state.legacyGlbColor) : bytes;
  if (kind === 'legacySvg' || kind === 'legacyView') {
    const colors = kind === 'legacySvg' ? state.legacySvgRegionColors : state.legacySvgViewRegionColors?.[viewId] || {};
    if (!Object.keys(colors).length) return bytes;
    const text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    return new TextEncoder().encode(recolorLegacySvgRegions(text, colors));
  }
  throw new TypeError('Legacy appearance: unsupported artefact');
}

export function legacyAppearanceKey(appearance) {
  return appearance ? JSON.stringify(createLegacyAssetAppearance(appearance)) : '';
}
